import React, { useState, useMemo } from 'react';
import { SystemLog } from '../../types';
import { useWBL } from '../../context/WBLContext';
import { 
  Search, 
  Download, 
  Activity, 
  Shield, 
  UserPlus, 
  UserMinus, 
  Sliders, 
  Briefcase, 
  Link as LinkIcon, 
  FileText, 
  ClipboardCheck, 
  Calendar, 
  CalendarX, 
  CalendarDays, 
  FilePlus, 
  CheckCircle2, 
  Award, 
  Clock, 
  CalendarCheck,
  User,
  Filter,
  RefreshCw,
  Inbox,
  Sparkles,
  ArrowUpDown,
  Eye,
  ArrowRight,
  ShieldCheck,
  ShieldAlert,
  Mail
} from 'lucide-react';

interface ActionMeta {
  label: string;
  category: 'Admin' | 'Placements' | 'Tasks' | 'Evaluations' | 'Interviews' | 'Logins' | 'FERPA & Compliance' | 'Other';
  color: string; // Tailwind prefix (e.g. 'slate', 'blue', etc.)
  iconName: string;
}

const formatTimestampEST = (timestampStr: string): string => {
  if (!timestampStr) return '';
  try {
    let dateStr = timestampStr;
    if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-')) {
      dateStr += 'Z';
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return timestampStr;

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(date);
    const partMap: Record<string, string> = {};
    parts.forEach(p => {
      partMap[p.type] = p.value;
    });

    const year = partMap.year;
    const month = partMap.month;
    const day = partMap.day;
    const hour = partMap.hour;
    const minute = partMap.minute;
    const period = partMap.dayPeriod || '';
    const tz = partMap.timeZoneName || 'EST';

    return `${year}-${month}-${day} ${hour}:${minute} ${period} ${tz}`;
  } catch (err) {
    return timestampStr;
  }
};

const formatRelativeTime = (timestampStr: string): string => {
  if (!timestampStr) return '';
  try {
    let dateStr = timestampStr;
    if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-')) {
      dateStr += 'Z';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const diffMs = Date.now() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const parseFerpaLogDetails = (details: string, userEmail: string) => {
  let studentName = '';
  let studentEmail = '';
  let signerName = '';
  let signerEmail = userEmail;
  let method = 'Digital Authorization';

  if (details.includes('overrode') || details.includes('OVERRIDE')) {
    method = 'Admin Verification Override';
    const match = details.match(/Admin\s+([^(]+)\s+\(([^)]+)\)\s+overrode.+for student\s+(.+)$/i);
    if (match) {
      signerName = match[1].trim();
      signerEmail = match[2].trim();
      studentName = match[3].trim();
    }
  } else if (details.includes('by parent') || details.includes('parent')) {
    method = 'Parent Digital Authorization';
    const match = details.match(/for student\s+([^(]+)\s+\(([^)]+)\)\s+by parent\s+([^(]+)\s+\(([^)]+)\)/i);
    if (match) {
      studentName = match[1].trim();
      studentEmail = match[2].trim();
      signerName = match[3].trim();
      signerEmail = match[4].trim();
    }
  } else if (details.includes('by student') || details.includes('Eligible Student')) {
    method = 'Eligible Student Self-Consent (18+)';
    const match = details.match(/by student\s+([^(]+)\s+\(([^)]+)\)/i);
    if (match) {
      studentName = match[1].trim();
      studentEmail = match[2].trim();
      signerName = studentName;
      signerEmail = studentEmail;
    }
  } else if (details.includes('granted by')) {
    const match = details.match(/granted by\s+([^(]+)\s+\(([^)]+)\)/i);
    if (match) {
      signerName = match[1].trim();
    }
  }

  if (!studentName && details.includes('for student')) {
    const parts = details.split('for student');
    if (parts[1]) {
      const studentPart = parts[1].split('by')[0].trim();
      studentName = studentPart;
    }
  }

  return {
    studentName: studentName || 'Student Profile',
    studentEmail: studentEmail || '',
    signerName: signerName || userEmail,
    signerEmail: signerEmail || userEmail,
    method
  };
};

const ACTION_METADATA: Record<string, ActionMeta> = {
  // --- LOGIN EVENTS ---
  USER_SIGN_IN: {
    label: 'User Logged In',
    category: 'Logins',
    color: 'emerald',
    iconName: 'UserPlus'
  },
  LOGIN: {
    label: 'User Logged In',
    category: 'Logins',
    color: 'emerald',
    iconName: 'UserPlus'
  },
  LOGIN_RESOLVE: {
    label: 'Linked & Signed In Account',
    category: 'Logins',
    color: 'emerald',
    iconName: 'UserPlus'
  },
  AUTO_CREATE_DEMO_USER_PROFILE: {
    label: 'Demo Profile Auto-Logged In',
    category: 'Logins',
    color: 'emerald',
    iconName: 'UserPlus'
  },

  // --- ADMIN & CONFIGURATION ---
  CREATE_TENANT: {
    label: 'Created New School/County Portal',
    category: 'Admin',
    color: 'slate',
    iconName: 'Shield'
  },
  CREATE_MOCK_USER: {
    label: 'Created New User Profile',
    category: 'Admin',
    color: 'blue',
    iconName: 'UserPlus'
  },
  DELETE_USER_PROFILE: {
    label: 'Deleted User Profile',
    category: 'Admin',
    color: 'red',
    iconName: 'UserMinus'
  },
  UPDATE_PROFILE: {
    label: 'Updated Profile Details',
    category: 'Admin',
    color: 'slate',
    iconName: 'Sliders'
  },
  SWITCH_TENANT: {
    label: 'Switched County Portal Workspace',
    category: 'Admin',
    color: 'slate',
    iconName: 'Sliders'
  },
  FERPA_CONSENT_GRANTED: {
    label: 'FERPA Release Granted',
    category: 'FERPA & Compliance',
    color: 'emerald',
    iconName: 'ShieldCheck'
  },
  FERPA_CONSENT: {
    label: 'Signed FERPA Release',
    category: 'FERPA & Compliance',
    color: 'emerald',
    iconName: 'ShieldCheck'
  },
  FERPA_CONSENT_OVERRIDE: {
    label: 'FERPA Admin Override',
    category: 'FERPA & Compliance',
    color: 'amber',
    iconName: 'ShieldAlert'
  },
  RESEND_FERPA_CONSENT: {
    label: 'Resent Parent FERPA Link',
    category: 'FERPA & Compliance',
    color: 'sky',
    iconName: 'Mail'
  },
  UPDATE_BRANDING: {
    label: 'Updated Portal Logo & Colors',
    category: 'Admin',
    color: 'purple',
    iconName: 'Sliders'
  },
  BULK_UPDATE_BRANDING: {
    label: 'Batch-Updated Multi-County Branding',
    category: 'Admin',
    color: 'purple',
    iconName: 'Sliders'
  },
  UPDATE_GLOBAL_BRANDING: {
    label: 'Updated Superadmin Global Identity',
    category: 'Admin',
    color: 'purple',
    iconName: 'Sliders'
  },

  // --- PLACEMENTS & OPPORTUNITIES ---
  POST_INTERNSHIP: {
    label: 'Listed New Internship Opportunity',
    category: 'Placements',
    color: 'indigo',
    iconName: 'Briefcase'
  },
  UPDATE_INTERNSHIP: {
    label: 'Updated Internship Details',
    category: 'Placements',
    color: 'indigo',
    iconName: 'Briefcase'
  },
  CREATE_PLACEMENT: {
    label: 'Assigned Student to Job Placement',
    category: 'Placements',
    color: 'indigo',
    iconName: 'LinkIcon'
  },
  RECOMMEND_STUDENT: {
    label: 'Recommended Student to Employer',
    category: 'Placements',
    color: 'indigo',
    iconName: 'LinkIcon'
  },
  UPDATE_AGREEMENT: {
    label: 'Signed/Updated Training Agreement',
    category: 'Placements',
    color: 'indigo',
    iconName: 'FileText'
  },
  HIRE_STUDENT: {
    label: 'Confirmed Job Offer & Hired Student',
    category: 'Placements',
    color: 'emerald',
    iconName: 'Award'
  },

  // --- APPLICATIONS & INTERVIEWS ---
  SUBMIT_APPLICATION: {
    label: 'Submitted Student Job Application',
    category: 'Interviews',
    color: 'violet',
    iconName: 'FileText'
  },
  REVIEW_APPLICATION: {
    label: 'Reviewed Student Application',
    category: 'Evaluations',
    color: 'emerald',
    iconName: 'ClipboardCheck'
  },
  SCHEDULE_INTERVIEW: {
    label: 'Scheduled Job Interview',
    category: 'Interviews',
    color: 'purple',
    iconName: 'Calendar'
  },
  CANCEL_INTERVIEW: {
    label: 'Cancelled Scheduled Interview',
    category: 'Interviews',
    color: 'red',
    iconName: 'CalendarX'
  },
  RESCHEDULE_INTERVIEW: {
    label: 'Rescheduled Job Interview',
    category: 'Interviews',
    color: 'amber',
    iconName: 'CalendarDays'
  },

  // --- TASKS & HOURS ---
  CREATE_TASK: {
    label: 'Assigned Coursework Task to Student',
    category: 'Tasks',
    color: 'sky',
    iconName: 'FilePlus'
  },
  SUBMIT_TASK: {
    label: 'Submitted Completed Task',
    category: 'Tasks',
    color: 'orange',
    iconName: 'CheckCircle2'
  },
  EVALUATE_TASK: {
    label: 'Graded Student Task Submission',
    category: 'Evaluations',
    color: 'emerald',
    iconName: 'Award'
  },
  SUBMIT_ATTENDANCE: {
    label: 'Logged Weekly Work Hours',
    category: 'Tasks',
    color: 'sky',
    iconName: 'Clock'
  },
  EVALUATE_ATTENDANCE: {
    label: 'Approved Student Work Hours',
    category: 'Evaluations',
    color: 'emerald',
    iconName: 'CalendarCheck'
  },
  UPDATE_EVALUATION: {
    label: 'Submitted Employer Performance Review',
    category: 'Evaluations',
    color: 'emerald',
    iconName: 'ClipboardCheck'
  },
};

const getActionInfo = (action: string): ActionMeta => {
  const normalized = action.toUpperCase();
  return ACTION_METADATA[normalized] || {
    label: action.replace(/_/g, ' '),
    category: 'Other',
    color: 'slate',
    iconName: 'Activity'
  };
};

export const EventsTabContent: React.FC<{ logs: SystemLog[] }> = ({ logs }) => {
  const { setCurrentView } = useWBL();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedRole, setSelectedRole] = useState<string>('All');
  const [timeRange, setTimeRange] = useState<string>('All');
  const [operationType, setOperationType] = useState<string>('All');
  const [selectedTenant, setSelectedTenant] = useState<string>('All');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Helper to resolve search / category mapping
  const logCategories = useMemo(() => {
    const counts: Record<string, number> = { All: logs.length };
    logs.forEach(log => {
      const info = getActionInfo(log.action);
      counts[info.category] = (counts[info.category] || 0) + 1;
    });
    return counts;
  }, [logs]);

  // Derive Actor Role Types
  const getActorRole = (email: string) => {
    const lower = email.toLowerCase();
    if (lower.includes('admin') || lower.includes('sarah.reg')) return 'Admin';
    if (lower.includes('student') || lower.includes('sam')) return 'Student';
    if (lower.includes('dan') || lower.includes('mary') || lower.includes('corp') || lower.includes('mentor') || lower.includes('employer')) return 'Employer';
    return 'User';
  };

  const getActorColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'Student': return 'bg-indigo-50 text-indigo-700 border-indigo-155';
      case 'Employer': return 'bg-amber-50 text-amber-800 border-amber-150';
      default: return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  const getActionColorClasses = (color: string) => {
    switch (color) {
      case 'slate': return { bg: 'bg-slate-50 text-slate-700 border-slate-200/60' };
      case 'blue': return { bg: 'bg-blue-50 text-blue-700 border-blue-100' };
      case 'red': return { bg: 'bg-rose-50 text-rose-700 border-rose-100' };
      case 'indigo': return { bg: 'bg-indigo-50 text-indigo-700 border-indigo-100' };
      case 'violet': return { bg: 'bg-purple-50 text-purple-700 border-purple-100' };
      case 'purple': return { bg: 'bg-violet-50 text-violet-750 border-violet-100' };
      case 'emerald': return { bg: 'bg-emerald-50 text-emerald-800 border-emerald-100' };
      case 'sky': return { bg: 'bg-sky-50 text-sky-750 border-sky-100' };
      case 'orange': return { bg: 'bg-orange-50/80 text-orange-700 border-orange-100' };
      case 'amber': return { bg: 'bg-amber-50 text-amber-800 border-amber-150' };
      default: return { bg: 'bg-slate-50 text-slate-750 border-slate-200' };
    }
  };

  const renderActionIcon = (iconName: string, color: string) => {
    const iconClass = "w-4 h-4";
    const mapped: Record<string, React.ReactNode> = {
      Shield: <Shield className={iconClass} />,
      UserPlus: <UserPlus className={iconClass} />,
      UserMinus: <UserMinus className={iconClass} />,
      Sliders: <Sliders className={iconClass} />,
      Briefcase: <Briefcase className={iconClass} />,
      LinkIcon: <LinkIcon className={iconClass} />,
      FileText: <FileText className={iconClass} />,
      ClipboardCheck: <ClipboardCheck className={iconClass} />,
      Calendar: <Calendar className={iconClass} />,
      CalendarX: <CalendarX className={iconClass} />,
      CalendarDays: <CalendarDays className={iconClass} />,
      FilePlus: <FilePlus className={iconClass} />,
      CheckCircle2: <CheckCircle2 className={iconClass} />,
      Award: <Award className={iconClass} />,
      ShieldCheck: <ShieldCheck className={iconClass} />,
      ShieldAlert: <ShieldAlert className={iconClass} />,
      Mail: <Mail className={iconClass} />,
      Clock: <Clock className={iconClass} />,
      CalendarCheck: <CalendarCheck className={iconClass} />,
    };
    return mapped[iconName] || <Activity className={iconClass} />;
  };

  // Filter logs logic
  const filteredLogs = useMemo(() => {
    return logs
      .filter(log => {
        const info = getActionInfo(log.action);
        const role = getActorRole(log.userEmail);

        // Term Match
        const matchesSearch = 
          log.action.toLowerCase().includes(searchTerm.toLowerCase()) || 
          log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
          info.label.toLowerCase().includes(searchTerm.toLowerCase());

        // Category Filter
        const matchesCategory = selectedCategory === 'All' || info.category === selectedCategory;

        // Role Filter
        const matchesRole = selectedRole === 'All' || role === selectedRole;

        // Tenant/Region Filter
        const matchesTenant = selectedTenant === 'All' || log.tenantId === selectedTenant;

        // Operation Type Filter
        let matchesOperation = true;
        if (operationType !== 'All') {
          const act = log.action.toUpperCase();
          if (operationType === 'CREATE') {
            matchesOperation = act.includes('CREATE') || act.includes('POST') || act.includes('SCHEDULE') || act.includes('ADD');
          } else if (operationType === 'UPDATE') {
            matchesOperation = (act.includes('UPDATE') || act.includes('SUBMIT') || act.includes('RESCHEDULE')) && !act.includes('EVALUATE');
          } else if (operationType === 'DELETE') {
            matchesOperation = act.includes('DELETE') || act.includes('CANCEL') || act.includes('REMOVE');
          } else if (operationType === 'EVALUATE') {
            matchesOperation = act.includes('EVALUATE') || act.includes('REVIEW') || act.includes('APPROVE') || act.includes('GRADE');
          }
        }

        // Time Range Filter
        let matchesTime = true;
        if (timeRange !== 'All') {
          const logDate = new Date(log.timestamp);
          const now = new Date();
          const diffMs = now.getTime() - logDate.getTime();
          const diffDays = diffMs / (1000 * 60 * 60 * 24);

          if (timeRange === 'Today' && diffDays > 1) matchesTime = false;
          else if (timeRange === 'Week' && diffDays > 7) matchesTime = false;
          else if (timeRange === 'Month' && diffDays > 30) matchesTime = false;
        }

        return matchesSearch && matchesCategory && matchesRole && matchesTime && matchesTenant && matchesOperation;
      })
      .sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
      });
  }, [logs, searchTerm, selectedCategory, selectedRole, timeRange, sortOrder, operationType, selectedTenant]);

  // Aggregate states for the mini-ledger KPIs
  const statsAggregate = useMemo(() => {
    let placementEventsCount = 0;
    let assignmentSubmissionsCount = 0;
    let approvalsCount = 0;
    let loginEventsCount = 0;
    let ferpaGrantedCount = 0;
    const uniqueUsers = new Set<string>();

    logs.forEach(log => {
      uniqueUsers.add(log.userEmail);
      const action = log.action.toUpperCase();
      if (action.includes('PLACEMENT') || action.includes('INTERNSHIP') || action.includes('AGREEMENT') || action.includes('HIRE')) {
        placementEventsCount++;
      }
      if (action.includes('TASK') || action.includes('SUBMIT')) {
        assignmentSubmissionsCount++;
      }
      if (action.includes('APPROVE') || action.includes('EVALUATE') || action.includes('REVIEW')) {
        approvalsCount++;
      }
      if (action.includes('LOGIN') || action.includes('SIGN_IN')) {
        loginEventsCount++;
      }
      if (action.includes('FERPA_CONSENT_GRANTED') || action.includes('FERPA_CONSENT') || action.includes('FERPA_RELEASE')) {
        ferpaGrantedCount++;
      }
    });

    return {
      total: logs.length,
      placements: placementEventsCount,
      tasks: assignmentSubmissionsCount,
      approvals: approvalsCount,
      activeUsers: uniqueUsers.size,
      logins: loginEventsCount,
      ferpaGranted: ferpaGrantedCount
    };
  }, [logs]);

  const tenantOptions = useMemo(() => {
    const list = new Set<string>();
    (logs || []).forEach(log => {
      if (log.tenantId) list.add(log.tenantId);
    });
    return ['All', ...Array.from(list)];
  }, [logs]);

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('All');
    setSelectedRole('All');
    setTimeRange('All');
    setOperationType('All');
    setSelectedTenant('All');
  };

  const renderQuickActionButton = (action: string) => {
    const act = action.toUpperCase();
    if (act.includes('FERPA')) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentView('users');
          }}
          className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-[9px] font-black uppercase tracking-wider rounded-lg transition-all border border-emerald-300 cursor-pointer flex items-center space-x-1 shadow-3xs"
          title="Open User Registry in Superadmin Console"
        >
          <ShieldCheck className="w-3 h-3 text-emerald-600" />
          <span>User Registry</span>
        </button>
      );
    }
    if (act.includes('PLACEMENT')) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentView('placements');
          }}
          className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[9px] font-black uppercase tracking-wider rounded-lg transition-all border border-indigo-200 cursor-pointer flex items-center space-x-1"
        >
          <span>Track placements</span>
        </button>
      );
    }
    if (act.includes('INTERNSHIP') || act.includes('OPPORTUNITY')) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentView('opportunities');
          }}
          className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[9px] font-black uppercase tracking-wider rounded-lg transition-all border border-amber-200 cursor-pointer flex items-center space-x-1"
        >
          <span>View Listings</span>
        </button>
      );
    }
    if (act.includes('APPLICATION')) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentView('applications');
          }}
          className="px-2.5 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 text-[9px] font-black uppercase tracking-wider rounded-lg transition-all border border-purple-200 cursor-pointer flex items-center space-x-1"
        >
          <span>Review Apps</span>
        </button>
      );
    }
    if (act.includes('INTERVIEW')) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentView('interviews');
          }}
          className="px-2.5 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 text-[9px] font-black uppercase tracking-wider rounded-lg transition-all border border-sky-200 cursor-pointer flex items-center space-x-1"
        >
          <span>View Schedules</span>
        </button>
      );
    }
    return (
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-2 select-none">
        Ledger Audited
      </span>
    );
  };

  const exportCSV = () => {
    const headers = ['ID', 'Region/Tenant', 'Actor Email', 'Actor Role', 'Action Key', 'Structured Action', 'Detailed Record', 'Timestamp'];
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(log => {
        const info = getActionInfo(log.action);
        const role = getActorRole(log.userEmail);
        return [
          log.id,
          log.tenantId,
          log.userEmail,
          role,
          log.action,
          `"${info.label.replace(/"/g, '""')}"`,
          `"${log.details.replace(/"/g, '""')}"`,
          `"${formatTimestampEST(log.timestamp)}"`
        ].join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wbl-system-ledger-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const categoriesList = ['All', 'FERPA & Compliance', 'Admin', 'Placements', 'Tasks', 'Evaluations', 'Interviews', 'Logins'];

  return (
    <div className="space-y-6">
      
      {/* KPI Stats overview banner */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
        <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col justify-between shadow-2xs hover:shadow-xs transition-shadow">
          <div className="flex items-center justify-between pb-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Ledger Records</span>
            <Activity className="w-4 h-4 text-[#0094c8]" />
          </div>
          <div>
            <div className="text-2xl font-black text-[#011d3c] tracking-tight">{statsAggregate.total}</div>
            <p className="text-[9px] text-slate-400 font-medium">Total logged transactions</p>
          </div>
        </div>

        <div 
          role="button"
          tabIndex={0}
          onClick={() => { setSelectedCategory('FERPA & Compliance'); setSearchTerm(''); }}
          className={`bg-white border p-4 rounded-2xl flex flex-col justify-between shadow-2xs hover:shadow-xs transition-all cursor-pointer ${
            selectedCategory === 'FERPA & Compliance' ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/10' : 'border-slate-200 hover:border-emerald-200'
          }`}
          title="Filter to FERPA Releases"
        >
          <div className="flex items-center justify-between pb-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700">FERPA Granted</span>
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <div className="text-2xl font-black text-emerald-900 tracking-tight">{statsAggregate.ferpaGranted}</div>
            <p className="text-[9px] text-slate-400 font-medium">Verified active releases</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col justify-between shadow-2xs hover:shadow-xs transition-shadow">
          <div className="flex items-center justify-between pb-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Placements</span>
            <Briefcase className="w-4 h-4 text-indigo-500" />
          </div>
          <div>
            <div className="text-2xl font-black text-[#011d3c] tracking-tight">{statsAggregate.placements}</div>
            <p className="text-[9px] text-slate-400 font-medium">Opportunity setup events</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col justify-between shadow-2xs hover:shadow-xs transition-shadow">
          <div className="flex items-center justify-between pb-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Verifications</span>
            <Award className="w-4 h-4 text-emerald-500" />
          </div>
          <div>
            <div className="text-2xl font-black text-[#011d3c] tracking-tight">{statsAggregate.approvals}</div>
            <p className="text-[9px] text-slate-400 font-medium">Completed sign-offs</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col justify-between shadow-2xs hover:shadow-xs transition-shadow">
          <div className="flex items-center justify-between pb-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Portal Logins</span>
            <User className="w-4 h-4 text-sky-500" />
          </div>
          <div>
            <div className="text-2xl font-black text-[#011d3c] tracking-tight">{statsAggregate.logins}</div>
            <p className="text-[9px] text-slate-400 font-medium">{statsAggregate.activeUsers} active users</p>
          </div>
        </div>
      </div>

      {/* Main event feed protocol block */}
      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg p-6 space-y-5">
        
        {/* Header Block with Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-5 gap-4">
          <div>
            <h2 className="text-lg font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-[#0094c8]" />
              <span>Events Ledger Protocol</span>
            </h2>
            <p className="text-[10px] text-slate-400 font-semibold tracking-wide uppercase mt-0.5">Audit-certified track log of region state transfers</p>
          </div>
          
          <button
            onClick={exportCSV}
            className="self-start sm:self-center px-4 py-2 bg-[#011d3c] hover:bg-[#0094c8] text-white text-[10px] uppercase tracking-widest font-black rounded-xl cursor-pointer flex items-center space-x-2 transition-all active:scale-95"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export Action Log</span>
          </button>
        </div>

        {/* Categories Tab Selector with Badges */}
        <div className="flex items-center overflow-x-auto pb-1.5 -mx-2 px-2 scrollbar-none gap-2">
          {categoriesList.map(cat => {
            const isActive = selectedCategory === cat;
            const count = logCategories[cat] || 0;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`py-1.5 px-3.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer border flex items-center space-x-1.5 ${
                  isActive 
                  ? 'bg-[#0094c8] hover:bg-[#007ba6] text-white border-[#0094c8] shadow-sm shadow-[#0094c8]/20' 
                  : 'bg-slate-50 hover:bg-slate-100 text-slate-500 border-slate-200'
                }`}
              >
                <span>{cat === 'All' ? 'All Activities' : cat}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[8px] font-mono leading-none ${isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-650'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filter Toolbar Box */}
        <div className="bg-slate-50/50 rounded-2xl border border-slate-100 p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
            
            {/* Search bar */}
            <div className="relative sm:col-span-4">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by action, email, details..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:ring-1 focus:ring-[#0094c8] placeholder:text-slate-450 text-slate-800 transition-all font-medium"
              />
            </div>

            {/* Operation Type Filter */}
            <div className="sm:col-span-4 flex items-center space-x-2">
              <span className="text-[10px] font-black text-slate-450 uppercase tracking-wider w-14 text-right">Operation:</span>
              <select
                value={operationType}
                onChange={(e) => setOperationType(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:ring-1 focus:ring-[#0094c8]"
              >
                <option value="All">All Operations / Intents</option>
                <option value="CREATE">Creation (Creations/Postings)</option>
                <option value="UPDATE">Modifications & Submissions</option>
                <option value="EVALUATE">Evaluations & Sign-offs</option>
                <option value="DELETE">Deletions & Cancellations</option>
              </select>
            </div>

            {/* Region / Tenant Filter */}
            <div className="sm:col-span-4 flex items-center space-x-2">
              <span className="text-[10px] font-black text-slate-450 uppercase tracking-wider w-14 text-right">Region:</span>
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-[#011d3c] outline-none focus:ring-1 focus:ring-[#0094c8] uppercase"
              >
                <option value="All">All Regional Partners</option>
                {tenantOptions.filter(t => t !== 'All').map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center pt-2 border-t border-slate-200/55">
            
            {/* Actor Filter */}
            <div className="sm:col-span-5 flex items-center space-x-2">
              <span className="text-[10px] font-black text-slate-450 uppercase tracking-wider w-14 text-right">Actor Type:</span>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:ring-1 focus:ring-[#0094c8]"
              >
                <option value="All">All Roles / Classes</option>
                <option value="Admin">Region Admins & System</option>
                <option value="Employer">Employers & Mentors</option>
                <option value="Student">Internship Students</option>
              </select>
            </div>

            {/* Time Picker */}
            <div className="sm:col-span-5 flex items-center space-x-2">
              <span className="text-[10px] font-black text-slate-450 uppercase tracking-wider w-14 text-right">Date Frame:</span>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:ring-1 focus:ring-[#0094c8]"
              >
                <option value="All">Any Historic Timestamp</option>
                <option value="Today">Recorded Today</option>
                <option value="Week">In Last 7 Days</option>
                <option value="Month">In Last 30 Days</option>
              </select>
            </div>

            {/* Controls */}
            <div className="sm:col-span-2 flex items-center justify-end space-x-2">
              <button
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                className="p-2.5 bg-white border border-slate-200 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer text-slate-600 flex items-center justify-center shadow-xs"
                title="Toggle Chronological Direction"
              >
                <ArrowUpDown className="w-4 h-4 text-slate-555" />
              </button>
              
              {(searchTerm || selectedCategory !== 'All' || selectedRole !== 'All' || timeRange !== 'All' || operationType !== 'All' || selectedTenant !== 'All') && (
                <button
                  onClick={handleResetFilters}
                  className="px-3 py-2.5 bg-rose-50 border border-rose-100 text-rose-600 hover:bg-rose-100 rounded-xl transition-colors cursor-pointer flex items-center justify-center font-black uppercase text-[10px] tracking-wider active:scale-95 shadow-2xs"
                  title="Reset active protocol filters"
                >
                  <span>Reset</span>
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Ledger Protocol Activity Feed Rows */}
        <div className="border border-slate-150 rounded-2xl overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50/80 border-b border-slate-150 text-slate-500 uppercase tracking-wider font-extrabold text-[9px]">
                <tr>
                  <th className="py-3.5 px-4 w-12"></th>
                  <th className="py-3.5 px-3 w-48">When</th>
                  <th className="py-3.5 px-4 w-52">Who Initiated</th>
                  <th className="py-3.5 px-4 w-60">Action Performed</th>
                  <th className="py-3.5 px-4">Outcome / Affected Record</th>
                  <th className="py-3.5 px-4 w-44 text-right pr-6">Quick Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-450 font-bold bg-slate-50/10">
                      <div className="flex flex-col items-center space-y-2">
                        <Inbox className="w-8 h-8 text-slate-300" />
                        <span className="text-[11px] uppercase tracking-wider">No matching event logs registered for this query</span>
                        <button 
                          onClick={handleResetFilters} 
                          className="mt-1 text-[10px] font-black text-[#0094c8] uppercase tracking-widest hover:underline"
                        >
                          Clear Active Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map(log => {
                    const info = getActionInfo(log.action);
                    const role = getActorRole(log.userEmail);
                    const roleClasses = getActorColor(role);
                    const colorCombo = getActionColorClasses(info.color);
                    const isExpanded = expandedLogId === log.id;
                    const isFerpaAction = log.action.includes('FERPA');
                    const ferpaMeta = isFerpaAction ? parseFerpaLogDetails(log.details, log.userEmail) : null;

                    return (
                      <React.Fragment key={log.id}>
                        <tr 
                          onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                          className={`border-l-4 transition-all cursor-pointer ${
                            isFerpaAction
                              ? isExpanded
                                ? 'bg-emerald-50/70 border-l-emerald-600 shadow-2xs'
                                : 'bg-emerald-50/20 hover:bg-emerald-50/45 border-l-emerald-500'
                              : isExpanded
                                ? 'bg-[#0094c8]/5 border-l-[#0094c8]'
                                : 'hover:bg-slate-50/70 border-l-transparent'
                          }`}
                        >
                          {/* Left Action Icon Circle */}
                          <td className="py-3.5 px-3 text-center">
                            <div className={`w-8 h-8 rounded-xl border flex items-center justify-center transition-transform hover:scale-105 duration-200 ${
                              isFerpaAction ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : colorCombo.bg
                            }`}>
                              {renderActionIcon(info.iconName, isFerpaAction ? 'emerald' : info.color)}
                            </div>
                          </td>

                          {/* Time */}
                          <td className="py-3.5 px-3 whitespace-nowrap">
                            <div className="font-mono text-slate-700 font-semibold text-[11px]">
                              {formatTimestampEST(log.timestamp)}
                            </div>
                            <div className="text-[9px] text-slate-400 font-bold mt-0.5 flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5 text-slate-400" />
                              <span>{formatRelativeTime(log.timestamp)}</span>
                            </div>
                          </td>

                          {/* Actor Email & Role Badge */}
                          <td className="py-3.5 px-4">
                            <div className="space-y-0.5">
                              <span className="font-extrabold text-slate-800 leading-tight block truncate max-w-[195px]" title={log.userEmail}>
                                {log.userEmail}
                              </span>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`inline-block text-[8px] font-black uppercase px-2 py-0.5 rounded-full border tracking-wider leading-none ${roleClasses}`}>
                                  {role}
                                </span>
                                {isFerpaAction && (
                                  <span className="text-[8px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.2 rounded border border-emerald-300">
                                    Signer
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Friendly Action Badge */}
                          <td className="py-3.5 px-4">
                            <div className="space-y-0.5">
                              {isFerpaAction ? (
                                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.8 rounded-md border bg-emerald-100 text-emerald-900 border-emerald-300 shadow-3xs">
                                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                  <span>{info.label}</span>
                                </span>
                              ) : (
                                <span className={`inline-block text-[9px] font-black uppercase tracking-wider px-2.5 py-0.8 rounded-md border ${colorCombo.bg}`}>
                                  {info.label}
                                </span>
                              )}
                              <span className="block text-[8px] text-slate-400 font-mono tracking-wider">{log.action}</span>
                            </div>
                          </td>

                          {/* Human readable details with click to expand notice */}
                          <td className="py-3.5 px-4 text-slate-650 pr-6">
                            {isFerpaAction && ferpaMeta ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 font-black text-[8px] uppercase tracking-wider border border-emerald-300">
                                    Student
                                  </span>
                                  <span className="font-extrabold text-slate-900 text-xs">
                                    {ferpaMeta.studentName}
                                  </span>
                                  {ferpaMeta.studentEmail && (
                                    <span className="text-[10px] text-slate-500 font-mono">({ferpaMeta.studentEmail})</span>
                                  )}
                                </div>
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                                  <span>Granted by: <strong className="text-slate-800 font-bold">{ferpaMeta.signerName}</strong></span>
                                  <span>•</span>
                                  <span className="text-emerald-700 font-semibold">{ferpaMeta.method}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between gap-1">
                                <span className="line-clamp-2 leading-relaxed text-[11px] font-medium">{log.details}</span>
                                <span className="text-[10px] text-[#0094c8] font-black shrink-0 ml-1 opacity-100 uppercase tracking-widest bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded-md">
                                  {isExpanded ? 'Hide' : 'Inspect'}
                                </span>
                              </div>
                            )}
                          </td>

                          {/* Quick Action Interactive router link */}
                          <td className="py-3.5 px-4 text-right pr-6" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end">
                              {renderQuickActionButton(log.action)}
                            </div>
                          </td>
                        </tr>

                        {/* Collapsible Meta Data Section */}
                        {isExpanded && (
                          <tr className={isFerpaAction ? 'bg-emerald-50/30' : 'bg-slate-50/50'}>
                            <td colSpan={6} className="p-4 px-8 border-t border-slate-100">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[11px]">
                                
                                {/* Info blocks */}
                                <div>
                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block mb-1">Audit Signature Details</span>
                                  <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1.5 font-mono text-[10px] text-slate-600">
                                    <div><span className="font-bold text-slate-400">LOG_ID:</span> {log.id}</div>
                                    <div><span className="font-bold text-slate-400">TENANT:</span> {log.tenantId}</div>
                                    <div><span className="font-bold text-slate-400">CATEGORY:</span> {info.category}</div>
                                    <div><span className="font-bold text-slate-400">TIMESTAMP (EST):</span> {formatTimestampEST(log.timestamp)}</div>
                                    <div><span className="font-bold text-slate-400">RELATIVE:</span> {formatRelativeTime(log.timestamp)}</div>
                                  </div>
                                </div>

                                <div className="md:col-span-2">
                                  <span className="text-[9px] font-black uppercase tracking-wider block mb-1 text-emerald-800">
                                    {isFerpaAction ? 'FERPA Statutory Release Verification (34 CFR Part 99)' : 'State Log Message Description'}
                                  </span>
                                  {isFerpaAction && ferpaMeta ? (
                                    <div className="bg-emerald-50/95 rounded-2xl border border-emerald-300 p-4 space-y-3.5 text-emerald-950 shadow-xs">
                                      <div className="flex items-center justify-between border-b border-emerald-200 pb-2.5">
                                        <div className="flex items-center space-x-2.5">
                                          <div className="p-1.5 bg-emerald-600 text-white rounded-xl shadow-2xs">
                                            <ShieldCheck className="w-5 h-5" />
                                          </div>
                                          <div>
                                            <span className="text-xs font-black uppercase tracking-wider text-emerald-950 block">
                                              Verified Statutory FERPA Authorization Release Active
                                            </span>
                                            <span className="text-[10px] text-emerald-700 font-medium">
                                              34 CFR § 99.30 Educational Privacy Disclosure Permitted
                                            </span>
                                          </div>
                                        </div>
                                        <span className="px-3 py-1 bg-emerald-600 text-white rounded-full text-[9px] font-black uppercase tracking-widest shadow-2xs">
                                          Active Release
                                        </span>
                                      </div>

                                      {/* 4-card grid breakdown: WHO SIGNED, AFFECTED STUDENT, WHEN RECORDED, METHOD */}
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-[11px]">
                                        <div className="bg-white/95 p-3 rounded-xl border border-emerald-200 space-y-1">
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                                            Authorized Signer (Grantee)
                                          </span>
                                          <span className="font-extrabold text-slate-900 block truncate text-xs">{ferpaMeta.signerName}</span>
                                          <span className="text-[10px] text-slate-500 font-mono block truncate">{ferpaMeta.signerEmail}</span>
                                          <span className="inline-block text-[8px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 mt-1">
                                            {role} Authorization
                                          </span>
                                        </div>

                                        <div className="bg-white/95 p-3 rounded-xl border border-emerald-200 space-y-1">
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                                            Protected Student Profile
                                          </span>
                                          <span className="font-extrabold text-slate-900 block truncate text-xs">{ferpaMeta.studentName}</span>
                                          {ferpaMeta.studentEmail && (
                                            <span className="text-[10px] text-slate-500 font-mono block truncate">{ferpaMeta.studentEmail}</span>
                                          )}
                                          <span className="inline-block text-[8px] font-bold uppercase px-2 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200 mt-1">
                                            Region: {log.tenantId}
                                          </span>
                                        </div>

                                        <div className="bg-white/95 p-3 rounded-xl border border-emerald-200 space-y-1">
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                                            When Granted (EST & Relative)
                                          </span>
                                          <span className="font-bold text-slate-800 block text-[11px]">{formatTimestampEST(log.timestamp)}</span>
                                          <span className="text-[10px] text-slate-500 font-semibold block">{formatRelativeTime(log.timestamp)}</span>
                                        </div>

                                        <div className="bg-white/95 p-3 rounded-xl border border-emerald-200 space-y-1">
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                                            Protocol & Superadmin Notice
                                          </span>
                                          <span className="font-extrabold text-emerald-800 block text-[11px]">{ferpaMeta.method}</span>
                                          <span className="text-[10px] text-emerald-700 block font-medium flex items-center gap-1">
                                            <Mail className="w-3 h-3 text-emerald-600 shrink-0" />
                                            <span>Email sent to Superadmin roster</span>
                                          </span>
                                        </div>
                                      </div>

                                      <div className="p-3 bg-white/95 rounded-xl border border-emerald-200 text-xs font-medium text-slate-700">
                                        <span className="font-extrabold text-emerald-950 block mb-0.5 uppercase tracking-wider text-[10px]">Audit Signature & Raw Ledger Trail:</span>
                                        <p className="text-slate-700 font-mono text-[10px] leading-relaxed bg-slate-50 p-2 rounded-lg border border-slate-100">{log.details}</p>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bg-white rounded-xl border border-slate-200 p-3 text-slate-705 leading-relaxed">
                                      <p className="font-semibold text-slate-800 pb-1">Historical Protocol Signature:</p>
                                      <p className="text-slate-600">{log.details}</p>
                                      <p className="text-[9px] text-[#0094c8] font-black uppercase tracking-widest mt-2">{info.label} Event Registered Successfully</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};
